import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { LEADS } from "../../constants/testIds";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { FormField } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import { EMPTY_PERSONAL_FORM, MemberPersonalFields, buildPersonalPayload, isPersonalFormValid } from "../desk/MemberPersonalFields";
import { MembershipPaymentStep } from "../desk/MembershipPaymentStep";
import { HealthGoalsFields } from "../desk/HealthGoalsFields";

const PHASE_DESCRIPTIONS = {
  converting: "Converting to a member…",
  personal: "Step 1 of 3 — Personal details",
  plan_payment: "Step 2 of 3 — Membership & payment",
  health: "Step 3 of 3 — Health & goals",
};

const PLAN_PAYMENT_TEST_IDS = {
  planSelect: LEADS.convertPlanSelect, startDateInput: LEADS.convertStartDateInput, discountInput: LEADS.convertDiscountInput,
  planNextButton: LEADS.convertPlanNextButton, skipButton: LEADS.convertSkipPlanButton, paymentMode: LEADS.convertPaymentMode,
  txnRefInput: LEADS.convertTxnRefInput, paymentAmount: LEADS.convertPaymentAmount, netPayable: LEADS.convertNetPayable,
  outstandingBalance: LEADS.convertOutstandingBalance, paymentSubmit: LEADS.convertPaymentSubmit,
  invoiceButton: LEADS.convertInvoiceButton, continueButton: LEADS.convertPaymentContinueButton,
};

// Product decision (explicit, user-confirmed): clicking "Convert" converts the lead to a member
// IMMEDIATELY — its stage flips to "converted" server-side right away (which is what makes it
// disappear from the Kanban board, per UX-CRM-02), it is NOT gated behind finishing this wizard.
// This wizard then walks through the SAME 3-step onboarding a brand-new walk-in member gets
// (Personal/KYC -> Membership & Payment -> Health & Goals) on that now-existing member, pre-
// filled from what the lead already told sales, instead of leaving a bare record behind.
export function ConvertLeadWizard({ lead, branchId, open, onOpenChange }) {
  const [phase, setPhase] = useState("converting");
  const [member, setMember] = useState(null);
  const [planInterest, setPlanInterest] = useState(null);
  const [name, setName] = useState("");
  const [personalForm, setPersonalForm] = useState(EMPTY_PERSONAL_FORM);
  const queryClient = useQueryClient();

  const convertMutation = useMutation({
    mutationFn: () => api.post(`/leads/${lead.id}/convert`, {}),
    onSuccess: async (res) => {
      setPlanInterest(res.data.plan_interest);
      const fresh = (await api.get(`/members/${res.data.member_id}`)).data;
      setMember(fresh);
      setName(fresh.profile.name);
      setPhase("personal");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (e) => { toast.error(formatApiError(e).message); onOpenChange(false); },
  });

  useEffect(() => {
    if (open && lead && phase === "converting" && !convertMutation.isPending && !convertMutation.isSuccess) {
      convertMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead]);

  const savePersonalMutation = useMutation({
    mutationFn: () => api.patch(`/members/${member.id}`, { name, ...buildPersonalPayload(personalForm) }),
    onSuccess: () => setPhase("plan_payment"),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const close = () => {
    onOpenChange(false);
    setPhase("converting"); setMember(null); setPlanInterest(null); setName(""); setPersonalForm(EMPTY_PERSONAL_FORM);
    convertMutation.reset();
  };
  const finish = (message) => { toast.success(message); queryClient.invalidateQueries({ queryKey: ["leads"] }); close(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto" data-testid={LEADS.convertWizard}>
        <DialogHeader>
          <DialogTitle>Convert lead{lead ? ` — ${lead.contact.name}` : ""}</DialogTitle>
          <DialogDescription>{PHASE_DESCRIPTIONS[phase]}</DialogDescription>
        </DialogHeader>

        {phase === "converting" && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={24} className="animate-spin" style={{ color: "#D6202B" }} />
          </div>
        )}

        {phase === "personal" && (
          <>
            <div className="space-y-4">
              <FormField label="Full name" required testId={LEADS.convertNameInput} value={name} onChange={setName} />
              <MemberPersonalFields form={personalForm} onChange={(k, v) => setPersonalForm((f) => ({ ...f, [k]: v }))} />
            </div>
            <DialogFooter>
              <SubmitButton
                testId={LEADS.convertPersonalNextButton} disabled={!name.trim() || !isPersonalFormValid(personalForm)}
                pending={savePersonalMutation.isPending} onClick={() => savePersonalMutation.mutate()}
                label="Continue"
              />
            </DialogFooter>
          </>
        )}

        {phase === "plan_payment" && member && (
          <MembershipPaymentStep
            member={member} branchId={branchId} allowSkip testIds={PLAN_PAYMENT_TEST_IDS} initialPlanId={planInterest}
            onSkip={() => setPhase("health")}
            onComplete={() => setPhase("health")}
          />
        )}

        {phase === "health" && member && (
          <HealthGoalsFields member={member} pageTestId={LEADS.convertHealthPage} onDone={() => finish(`${member.profile.name} converted to a member.`)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
